// src/components/strumenti/AmmortamentoBene.js
// Quota annua = Costo storico × Aliquota fiscale. Nel primo esercizio, per prassi fiscale
// italiana, la quota è generalmente ridotta al 50% (art. 102 TUIR).
// Unico strumento di Finanza Aziendale con archivio: i calcoli salvati sono condivisi in
// azienda e visibili a tutti tranne gli utenti con ruolo "Operativo".
import React, { useState, useEffect } from "react";
import { collection, query, where, onSnapshot, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { messaggioErrore } from "../../utils/erroriUtente";
import { useAuth } from "../../contexts/AuthContext";
import { useOrg } from "../../contexts/OrgContext";
import { useToast } from "../../utils/Toast";
import { RefreshCw, Printer, Save, Archive } from "lucide-react";
import { leggiNumero } from "../../utils/numeriStrumenti";
import { durataAmmortamento } from "../../utils/calcoliStrumenti";

const inp = { padding:"10px 12px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:14, fontFamily:"'Inter',sans-serif", outline:"none", width:"100%" };
const lbl = { fontSize:11, fontWeight:600, color:"#6b7280", display:"block", marginBottom:4 };

const ALIQUOTE_COMUNI = [
  { label:"Personalizzata", val:"" },
  { label:"Impianti e macchinari generici — 15%", val:"15" },
  { label:"Attrezzatura varia e minuta — 25%", val:"25" },
  { label:"Autoveicoli da trasporto — 20%", val:"20" },
  { label:"Mobili e arredi ufficio — 12%", val:"12" },
  { label:"Computer e macchine ufficio elettroniche — 20%", val:"20" },
];

export default function AmmortamentoBene() {
  const { currentUser, isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId } = useOrg();
  const { addToast } = useToast();

  const [mioRuoloGlobale, setMioRuoloGlobale] = useState(null);
  const [archivio, setArchivio] = useState([]);
  const [salvando, setSalvando] = useState(false);

  const [denominazione, setDenominazione] = useState("");
  const [note, setNote] = useState("");
  const [costo, setCosto] = useState("");
  const [aliquota, setAliquota] = useState("15");
  const [primoAnnoRidotto, setPrimoAnnoRidotto] = useState(true);

  useEffect(() => {
    if (!currentUser || !effectiveOrgId) return;
    return onSnapshot(query(collection(db,"dipendenti"), where("organizationId","==",effectiveOrgId), where("nexoraUid","==",currentUser.uid)),
      snap => setMioRuoloGlobale(snap.empty ? null : snap.docs[0].data().ruolo || null));
  }, [currentUser, effectiveOrgId]);

  const puoVedereArchivio = isOrgAdmin || isPlatformOwner || mioRuoloGlobale === "Non operativo";

  useEffect(() => {
    if (!effectiveOrgId || !puoVedereArchivio) return;
    return onSnapshot(query(collection(db,"ammortamentiBeni"), where("organizationId","==",effectiveOrgId)),
      snap => {
        const lista = snap.docs.map(d=>({id:d.id,...d.data()}));
        lista.sort((a,b) => (b.createdAtMillis||0)-(a.createdAtMillis||0));
        setArchivio(lista);
      });
  }, [effectiveOrgId, puoVedereArchivio]);

  const C = leggiNumero(costo)||0;
  const al = leggiNumero(aliquota)||0;

  const quotaPiena = C*(al/100);
  const quotaPrimoAnno = primoAnnoRidotto ? quotaPiena/2 : quotaPiena;
  const anniAmmortamento = durataAmmortamento(al, primoAnnoRidotto); // W442: con il primo anno a metà serve un anno in più

  const salvaInArchivio = async () => {
    if (!denominazione.trim() || C<=0) return;
    setSalvando(true);
    try {
      await addDoc(collection(db,"ammortamentiBeni"), {
        organizationId: effectiveOrgId,
        denominazione, note,
        costo: C, aliquota: al, primoAnnoRidotto,
        quotaPrimoAnno, quotaPiena, anniAmmortamento,
        creatoDaUid: currentUser?.uid, creatoDaNome: currentUser?.displayName || "",
        createdAt: serverTimestamp(), createdAtMillis: Date.now(),
      });
      addToast("Salvato in archivio ✅", "success");
    } catch (err) {
      addToast(messaggioErrore(err), "error");
    }
    setSalvando(false);
  };

  const stampaPDF = () => {
    const win = window.open("", "_blank");
    const oggi = new Date().toLocaleDateString("it-IT", { day:"numeric", month:"long", year:"numeric" });
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Ammortamento — ${denominazione}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:Arial,sans-serif;color:#111;padding:14mm;font-size:12px;}
      @media screen{body{max-width:800px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      @page{size:A4;margin:14mm;}
      h1{font-size:18px;color:#059669;margin-bottom:2px;}
      .sub{font-size:11px;color:#6b7280;margin-bottom:16px;}
      table{width:100%;border-collapse:collapse;margin-bottom:14px;}
      td,th{border:1px solid #e5e7eb;padding:6px 10px;text-align:left;font-size:12px;}
      th{background:#f9fafb;width:45%;color:#374151;}
      .risultato{background:#ecfdf5;border:1px solid #a7f3d0;border-radius:8px;padding:16px;text-align:center;margin-bottom:14px;}
    </style></head><body>
      <h1>📉 Ammortamento Bene Strumentale</h1>
      <div class="sub">Nexiora — Strumenti Tecnici · Generato il ${oggi}</div>
      <table>
        <tr><th>Denominazione bene</th><td>${denominazione || "—"}</td></tr>
        <tr><th>Note</th><td>${note || "—"}</td></tr>
      </table>
      <table>
        <tr><th>Costo storico</th><td>${costo} €</td></tr>
        <tr><th>Aliquota di ammortamento</th><td>${aliquota}%</td></tr>
        <tr><th>Primo esercizio ridotto al 50%</th><td>${primoAnnoRidotto ? "Sì" : "No"}</td></tr>
      </table>
      <div class="risultato">
        <div style="display:flex;justify-content:space-around;margin-bottom:10px;">
          <div><div style="font-size:11px;color:#9ca3af;">Quota primo anno</div><div style="font-size:20px;font-weight:800;color:#059669;">${quotaPrimoAnno.toLocaleString("it-IT",{maximumFractionDigits:2})} €</div></div>
          <div><div style="font-size:11px;color:#9ca3af;">Quota annua a regime</div><div style="font-size:20px;font-weight:800;color:#374151;">${quotaPiena.toLocaleString("it-IT",{maximumFractionDigits:2})} €</div></div>
        </div>
        <div style="font-size:12px;color:#6b7280;border-top:1px solid #a7f3d0;padding-top:8px;">Durata indicativa del piano: circa ${anniAmmortamento} anni</div>
      </div>
      <div style="font-size:10px;color:#9ca3af;">Le aliquote sono indicative — quella fiscalmente corretta va sempre verificata sulle tabelle ministeriali (D.M. 31/12/1988) o con il commercialista.</div>
      
    </body></html>`);
    win.document.close();
    setTimeout(()=>{
      if (win.closed) return;
      const style = win.document.createElement("style");
      style.textContent = "@media print { .no-print-btn { display:none !important; } }";
      win.document.head.appendChild(style);
      const btn = win.document.createElement("button");
      btn.textContent = "🖨️ Stampa";
      btn.className = "no-print-btn";
      btn.onclick = () => win.print();
      btn.style.cssText = "position:fixed;top:16px;right:16px;z-index:9999;padding:10px 20px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(0,0,0,.2);";
      win.document.body.appendChild(btn);
    }, 50);
  };

  return (
    <div>
      <div style={{ background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:8, padding:12, marginBottom:16 }}>
        <div style={{ marginBottom:8 }}>
          <label style={lbl}>Denominazione bene (necessaria per stampa e archivio)</label>
          <input value={denominazione} onChange={e=>setDenominazione(e.target.value)} style={inp} placeholder="Es. Escavatore Caterpillar 305 — acquistato 15/03/2026"/>
        </div>
        <div>
          <label style={lbl}>Note (facoltativo)</label>
          <input value={note} onChange={e=>setNote(e.target.value)} style={inp}/>
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
        <div>
          <label style={lbl}>Costo storico del bene (€)</label>
          <input value={costo} onChange={e=>setCosto(e.target.value)} style={inp} placeholder="Es. 15000"/>
        </div>
        <div>
          <label style={lbl}>Aliquota di ammortamento (%)</label>
          <input value={aliquota} onChange={e=>setAliquota(e.target.value)} style={inp}/>
        </div>
      </div>
      <div style={{ marginBottom:12 }}>
        <label style={lbl}>Categoria di riferimento (facoltativo, per orientarsi)</label>
        <select onChange={e=>{ if(e.target.value) setAliquota(e.target.value); }} style={inp} defaultValue="">
          <option value="">— Seleziona una categoria comune —</option>
          {ALIQUOTE_COMUNI.filter(a=>a.val).map(a => <option key={a.label} value={a.val}>{a.label}</option>)}
        </select>
      </div>
      <label style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16, cursor:"pointer" }}>
        <input type="checkbox" checked={primoAnnoRidotto} onChange={e=>setPrimoAnnoRidotto(e.target.checked)} style={{ accentColor:"#059669" }}/>
        <span style={{ fontSize:12, color:"#374151" }}>Primo esercizio con quota ridotta al 50% (prassi comune, art. 102 TUIR)</span>
      </label>

      <div style={{ background:"#ecfdf5", border:"1px solid #a7f3d0", borderRadius:10, padding:"14px 16px" }}>
        <div style={{ display:"flex", justifyContent:"space-around", marginBottom:10 }}>
          <div style={{textAlign:"center"}}>
            <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600 }}>Quota primo anno</div>
            <div style={{ fontSize:18, fontWeight:800, color:"#059669" }}>{C>0 ? quotaPrimoAnno.toLocaleString("it-IT",{maximumFractionDigits:2}) : "—"} €</div>
          </div>
          <div style={{textAlign:"center"}}>
            <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600 }}>Quota annua a regime</div>
            <div style={{ fontSize:18, fontWeight:800, color:"#374151" }}>{C>0 ? quotaPiena.toLocaleString("it-IT",{maximumFractionDigits:2}) : "—"} €</div>
          </div>
        </div>
        {anniAmmortamento && (
          <div style={{ textAlign:"center", borderTop:"1px solid #a7f3d0", paddingTop:10, fontSize:12, color:"#6b7280" }}>
            Durata indicativa del piano di ammortamento: circa {anniAmmortamento} anni
          </div>
        )}
      </div>
      <div style={{ fontSize:10, color:"#9ca3af", marginTop:8, textAlign:"center" }}>
        Le aliquote suggerite sono indicative e di uso comune — quella fiscalmente corretta per il bene specifico va sempre verificata sulle tabelle ministeriali (D.M. 31/12/1988) o con il commercialista.
      </div>

      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:12, flexWrap:"wrap", gap:8 }}>
        <button onClick={()=>{setCosto("");setAliquota("15");setPrimoAnnoRidotto(true);setDenominazione("");setNote("");}}
          style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"#9ca3af", background:"none", border:"none", cursor:"pointer" }}>
          <RefreshCw size={12}/> Azzera
        </button>
        <div style={{ display:"flex", gap:8 }}>
          {puoVedereArchivio && (
            <button onClick={salvaInArchivio} disabled={!denominazione.trim() || C<=0 || salvando}
              title={!denominazione.trim() || C<=0 ? "Inserisci denominazione e costo per salvare" : "Salva in Archivio"}
              style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, fontWeight:700,
                color: (denominazione.trim()&&C>0) ? "#2563eb" : "#bfdbfe",
                background: (denominazione.trim()&&C>0) ? "#eff6ff" : "#f9fafb",
                border:`1px solid ${(denominazione.trim()&&C>0) ? "#bfdbfe" : "#e5e7eb"}`,
                borderRadius:8, padding:"7px 14px", cursor: (denominazione.trim()&&C>0) ? "pointer" : "not-allowed" }}>
              <Save size={13}/> {salvando ? "Salvo..." : "Salva in Archivio"}
            </button>
          )}
          <button onClick={stampaPDF} disabled={!denominazione.trim()}
            title={!denominazione.trim() ? "Inserisci la denominazione per stampare" : "Stampa PDF"}
            style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, fontWeight:700,
              color: denominazione.trim() ? "#059669" : "#a7f3d0",
              background: denominazione.trim() ? "#ecfdf5" : "#f9fafb",
              border:`1px solid ${denominazione.trim() ? "#a7f3d0" : "#e5e7eb"}`,
              borderRadius:8, padding:"7px 14px", cursor: denominazione.trim() ? "pointer" : "not-allowed" }}>
            <Printer size={13}/> Stampa PDF
          </button>
        </div>
      </div>

      {puoVedereArchivio && archivio.length>0 && (
        <div style={{ marginTop:24, paddingTop:16, borderTop:"1px solid #e5e7eb" }}>
          <div style={{ fontSize:13, fontWeight:700, color:"#374151", display:"flex", alignItems:"center", gap:6, marginBottom:10 }}>
            <Archive size={14}/> Archivio ammortamenti ({archivio.length})
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:6, maxHeight:260, overflowY:"auto" }}>
            {archivio.map(a => (
              <div key={a.id} style={{ background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:8, padding:"8px 12px", display:"flex", justifyContent:"space-between", alignItems:"center", gap:10 }}>
                <div style={{ minWidth:0 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:"#374151", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{a.denominazione}</div>
                  <div style={{ fontSize:11, color:"#9ca3af" }}>
                    {a.costo?.toLocaleString("it-IT")} € · {a.aliquota}% · {a.creatoDaNome || "—"} · {a.createdAtMillis ? new Date(a.createdAtMillis).toLocaleDateString("it-IT") : ""}
                  </div>
                </div>
                <div style={{ fontSize:13, fontWeight:800, color:"#059669", whiteSpace:"nowrap" }}>{a.quotaPiena?.toLocaleString("it-IT",{maximumFractionDigits:2})} €/anno</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
